// Thin, icon-only navigation rail (Superhuman style). Replaces the old top
// HeaderWithNavigation bar. Fixed to the left edge, full height:
//   top    — Ethosphere brand mark
//   nav    — Workspace · Threads · Audio Batches ·
//            Test Audio (feature-gated) · Admin (admin-only)
//   bottom — profile button → Voice Signature + Log Out
//
// Labels live in hover tooltips since the rail is collapsed by design. Uses the
// app's existing blue accent so it stays coherent with the rest of the UI.

import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Tooltip,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { getTestAudioFeatureStatus } from "../services/testAudioService";
import { getAssociateCoachingFeatureStatus } from "../services/associateCoaching";
import {
  ThreadsIcon,
  AudioIcon,
  FlaskIcon,
  AdminIcon,
  LogoutIcon,
  SignatureIcon,
  EngagementIcon,
} from "./navIcons";

export const RAIL_WIDTH = "60px";

function RailIcon({ label, active, onClick, children }) {
  return (
    <Tooltip label={label} placement="right" hasArrow openDelay={150} fontSize="xs">
      <Box
        as="button"
        onClick={onClick}
        w="42px"
        h="42px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="12px"
        bg={active ? "blue.50" : "transparent"}
        color={active ? "blue.600" : "gray.400"}
        transition="all 0.12s ease"
        _hover={{ bg: active ? "blue.50" : "gray.100", color: active ? "blue.600" : "gray.700" }}
        aria-label={label}
        aria-current={active ? "page" : undefined}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

const SideRail = ({ onSignOut, onEditSignature, userName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, userInfo } = useUser();
  const isManager = userInfo?.role === "manager" && !isAdmin;

  // Test Audio Files tab — dev-only / Ethosphere-org-only, gated on the
  // feature-status endpoint (always 200; a failed fetch hides the tab).
  const [testAudioEnabled, setTestAudioEnabled] = useState(false);
  const [coachingEnabled, setCoachingEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { enabled } = await getTestAudioFeatureStatus();
        if (!cancelled) setTestAudioEnabled(!!enabled);
      } catch {
        if (!cancelled) setTestAudioEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isManager || isAdmin) {
      setCoachingEnabled(false);
      return () => { cancelled = true; };
    }
    getAssociateCoachingFeatureStatus()
      .then(({ enabled }) => { if (!cancelled) setCoachingEnabled(!!enabled); })
      .catch(() => { if (!cancelled) setCoachingEnabled(false); });
    return () => { cancelled = true; };
  }, [isAdmin, isManager]);

  const items = [
    ...((userInfo?.role === "manager" || isAdmin)
      ? [{ label: "Engagement", path: "/manager/engagement", Icon: EngagementIcon, alsoMatch: "/manager/" }]
      : []),
    ...(!isManager ? [
      // Workspace (spike) UI temporarily removed from nav — see App.js.
      ...(coachingEnabled
        ? [{ label: "Coaching", path: "/coaching", Icon: EngagementIcon }]
        : []),
      { label: "Threads", path: "/threads", Icon: ThreadsIcon, alsoMatch: "/threads/" },
      { label: "Audio Batches", path: "/audio-batches", Icon: AudioIcon },
      ...(testAudioEnabled
        ? [{ label: "Test Audio", path: "/test-audio", Icon: FlaskIcon }]
        : []),
    ] : []),
    ...(isAdmin ? [{ label: "Admin", path: "/admin", Icon: AdminIcon }] : []),
  ];

  const isActive = (item) =>
    location.pathname === item.path ||
    (item.alsoMatch && location.pathname.startsWith(item.alsoMatch));

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      left={0}
      bottom={0}
      w={RAIL_WIDTH}
      bg="white"
      borderRightWidth="1px"
      borderRightColor="gray.100"
      display="flex"
      flexDirection="column"
      alignItems="center"
      py={3}
      zIndex={1000}
    >
      {/* brand */}
      <Box
        cursor="pointer"
        onClick={() => navigate("/")}
        mb={4}
        flex="0 0 auto"
        _hover={{ opacity: 0.85 }}
        transition="opacity 0.15s"
      >
        <Image
          src="/Logo.png"
          alt="Ethosphere"
          boxSize="30px"
          borderRadius="8px"
          fallbackSrc="https://via.placeholder.com/30x30/3B82F6/ffffff?text=E"
        />
      </Box>

      {/* nav */}
      <VStack spacing={1.5} flex="0 0 auto">
        {items.map((item) => {
          const { Icon } = item;
          return (
            <RailIcon
              key={item.path}
              label={item.label}
              active={isActive(item)}
              onClick={() => navigate(item.path)}
            >
              <Icon boxSize="21px" />
            </RailIcon>
          );
        })}
      </VStack>

      <Box flex="1" />

      {/* profile → identity + actions */}
      <Menu placement="right-end">
        <Tooltip label="Account" placement="right" hasArrow openDelay={150} fontSize="xs">
          <MenuButton
            as={Box}
            cursor="pointer"
            borderRadius="full"
            _hover={{ opacity: 0.85 }}
            transition="opacity 0.15s"
          >
            <Avatar size="sm" name={userName} bg="blue.500" color="white" fontSize="xs" fontWeight="semibold" />
          </MenuButton>
        </Tooltip>
        <MenuList border="1px solid" borderColor="gray.100" shadow="lg" borderRadius="lg" py={1} minW="200px">
          <Box px={4} py={2}>
            <Text fontSize="xs" color="gray.400" fontWeight="medium">Signed in as</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800" isTruncated maxW="170px">{userName}</Text>
          </Box>
          <MenuDivider my={1} />
          <MenuItem icon={<SignatureIcon boxSize="15px" />} fontSize="sm" onClick={onEditSignature} _hover={{ bg: "gray.50" }} py={2} px={4} color="gray.700">
            Voice Signature
          </MenuItem>
          <MenuDivider my={1} />
          <MenuItem icon={<LogoutIcon boxSize="15px" />} onClick={onSignOut} fontSize="sm" fontWeight="medium" color="red.500" _hover={{ bg: "red.50", color: "red.600" }} py={2} px={4}>
            Log Out
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default SideRail;
